import 'uikit/dist/css/uikit.min.css';
import '@csstools/normalize.css';
import '@/styles/variables.css';
import '@/styles/globals.css';
import { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { ReactElement, ReactNode, useMemo, useState } from 'react';
import AuthContext from '@/components/AuthContext';
import AuthRental from '@/components/AuthRental';
import AuthTournament from '@/components/AuthTournament';
import Header from '@/components/Header';
import Loading from '@/components/Loading';
import Processing, { useProcessing } from '@/components/Processing';
import Settings from '@/components/Settings';
import Toast, { useToast } from '@/components/Toast';
import UserChanged from '@/components/UserChanged';
import { URL_ALLOW } from '@/constants';
import FeedbackContext from '@/contexts/feedback';
import SchoolContext from '@/contexts/school';
import useAdminSchool from '@/hooks/useAdminSchool';

// UIkitの初期化
import useGetAllLevel from '@/hooks/useGetAllLevel';
import useGetSystemNotice from '@/hooks/useGetSystemNotice';
import FlexLayout from '@/layouts/FlexLayout';
import { UIkit, Icons } from '@/plugins/uikit/UIkit';
UIkit.use(Icons);

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const { data, error, school } = useAdminSchool();
  const [{ data: levels }] = useGetAllLevel(school?.schema);
  const getLayout = Component.getLayout ?? ((page) => page);

  const { notice: systemNotice } = useGetSystemNotice();
  const router = useRouter();
  const [heightNotice, setHeightNotice] = useState<number>(0);

  // get padding for container
  const paddingTop = useMemo(() => {
    if (URL_ALLOW.includes(router.pathname)) {
      return heightNotice;
    }
    if (systemNotice) {
      return heightNotice + 82;
    }
    return 82;
  }, [heightNotice, router.pathname, systemNotice]);

  const [processing, toggleProcessing] = useProcessing(false);
  const [toast, showToast] = useToast();

  if (!data && !error && !school) {
    return <Loading />;
  }

  if (error) {
    return <div>Error</div>;
  }

  if (data) {
    if (school) {
      const { schema, slug, id, db, tenant_id, is_tournament_contract, is_school_contract, is_rental_contract } =
        school;

      return (
        <SchoolContext.Provider
          value={{
            schema,
            slug,
            id,
            db,
            tenant_id,
            levels,
            is_tournament_contract,
            is_school_contract,
            is_rental_contract,
          }}>
          <FeedbackContext.Provider value={{ toggleProcessing, showToast, heightNotice, setHeightNotice }}>
            <AuthContext>
              <UserChanged>
                <AuthTournament>
                  <AuthRental>
                    <Settings>
                      <Header />
                      <FlexLayout
                        minHeight={'100vh'}
                        padding={`${paddingTop}px 0px 0px 0px`}
                        type='column'
                        justifyContent={'space-between'}>
                        {getLayout(<Component {...pageProps} />)}
                      </FlexLayout>
                    </Settings>
                  </AuthRental>
                </AuthTournament>
              </UserChanged>
            </AuthContext>

            {/* Toast */}
            <Toast visible={toast.visible} status={toast.status} message={toast.message} />

            {/* 処理中 */}
            <Processing visible={processing} />
          </FeedbackContext.Provider>
        </SchoolContext.Provider>
      );
    } else {
      return getLayout(<Component {...pageProps} />);
    }
  }
}

export default MyApp;
